import React, { useMemo, useRef, useState } from 'react';
import {
  FunnelBanner,
  FunnelModal,
  FunnelPopup,
  type FunnelOffer,
  useFunnelContext,
} from '@template-project/storefront-funnel';
import { useCampaignExperience } from '../use-campaign-experience';
import type { CampaignEvaluationContext, RuntimeCampaign } from '../types';

interface CampaignExperienceProps {
  pageType: string;
  context: CampaignEvaluationContext;
  onFreeGiftAccept?: (campaign: RuntimeCampaign) => void | Promise<void>;
}

const EMPTY_OFFER: FunnelOffer = {
  id: 'campaign-offer',
  name: 'Campaign CTA',
  productId: '',
  variantId: '',
  headline: 'Campaign',
  description: '',
  imageUrl: '',
  ctaText: 'Continue',
  declineText: 'Dismiss',
  pricingType: 'fixed',
  pricingValue: null,
  originalPrice: null,
};

const FREE_GIFT_ACCEPT_ERROR = 'We could not add this gift to your cart. Please try again.';

export function CampaignExperience({ pageType, context, onFreeGiftAccept }: CampaignExperienceProps) {
  const { activeExperience } = useFunnelContext();
  const acceptingCampaignRef = useRef<string | null>(null);
  const overlayCampaignIdRef = useRef<string | null>(null);
  const [acceptingCampaignId, setAcceptingCampaignId] = useState<string | null>(null);
  const [freeGiftError, setFreeGiftError] = useState<{ campaignId: string; message: string } | null>(null);
  const {
    loading,
    bannerCampaign,
    bannerPageContent,
    bannerDisplaySettings,
    overlayCampaign,
    overlayPageContent,
    overlayDisplaySettings,
    dismissCampaign,
  } = useCampaignExperience(pageType, context, activeExperience);

  overlayCampaignIdRef.current = overlayCampaign?.id ?? null;

  const buildFallbackOffer = useMemo(
    () => (campaign: RuntimeCampaign | null): FunnelOffer => {
      if (!campaign) return EMPTY_OFFER;
      return {
        ...EMPTY_OFFER,
        id: campaign.id,
        name: campaign.name,
        headline: campaign.name,
        description: campaign.description ?? '',
      };
    },
    [],
  );

  if (loading) return null;

  const bannerOffer = buildFallbackOffer(bannerCampaign);
  const overlayOffer = buildFallbackOffer(overlayCampaign);

  const bannerNode = bannerCampaign ? (
    <FunnelBanner
      offer={bannerOffer}
      pageContent={bannerPageContent}
      displaySettings={bannerDisplaySettings}
      onAccept={() => dismissCampaign(bannerCampaign.id)}
      onDecline={() => dismissCampaign(bannerCampaign.id)}
      onDismiss={() => dismissCampaign(bannerCampaign.id)}
      accepting={false}
      errorMessage={null}
    />
  ) : null;

  if (!overlayCampaign) {
    return bannerNode;
  }

  const handleOverlayAccept = async () => {
    if (acceptingCampaignRef.current === overlayCampaign.id) return;

    if (overlayCampaign.campaignType !== 'freegift_popup' || !onFreeGiftAccept) {
      setFreeGiftError(null);
      dismissCampaign(overlayCampaign.id);
      return;
    }

    setFreeGiftError(null);
    acceptingCampaignRef.current = overlayCampaign.id;
    setAcceptingCampaignId(overlayCampaign.id);
    try {
      await onFreeGiftAccept(overlayCampaign);
      if (overlayCampaignIdRef.current === overlayCampaign.id) {
        dismissCampaign(overlayCampaign.id);
      }
    } catch {
      // Keep the campaign visible so the storefront can retry or surface its own cart error state.
      setFreeGiftError({ campaignId: overlayCampaign.id, message: FREE_GIFT_ACCEPT_ERROR });
    } finally {
      if (acceptingCampaignRef.current === overlayCampaign.id) {
        acceptingCampaignRef.current = null;
        setAcceptingCampaignId(null);
      }
    }
  };

  const commonOverlayProps = {
    offer: overlayOffer,
    pageContent: overlayPageContent,
    displaySettings: overlayDisplaySettings,
    onAccept: handleOverlayAccept,
    onDecline: () => {
      setFreeGiftError(null);
      dismissCampaign(overlayCampaign.id);
    },
    onDismiss: () => {
      setFreeGiftError(null);
      dismissCampaign(overlayCampaign.id);
    },
    accepting: acceptingCampaignId === overlayCampaign.id,
    errorMessage:
      overlayCampaign.campaignType === 'freegift_popup' && freeGiftError?.campaignId === overlayCampaign.id
        ? freeGiftError.message
        : null,
  };

  const overlayNode =
    overlayCampaign.campaignType === 'leads_popup' ? (
      <FunnelPopup {...commonOverlayProps} />
    ) : (
      <FunnelModal {...commonOverlayProps} />
    );

  return (
    <>
      {bannerNode}
      {overlayNode}
    </>
  );
}
